import { useState, useEffect, useCallback, useRef } from 'react'
import { Bot, Play, PlayCircle, Trash2, UserPlus, RefreshCw, CheckCircle, XCircle, Clock, Zap, Clapperboard, ChevronRight, AlertTriangle, Shield, Mail, Phone, X } from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

interface AgentStatus {
  id: string
  firstName: string
  lastName: string
  role: string
  jobTitle: string
  department: string
  personality: string
  seeded: boolean
  userId: string | null
  lastAction: SimLog | null
}

interface SimLog {
  id: string
  agent_id: string
  agent_name: string
  tool: string
  entity_type: string | null
  entity_id: string | null
  description: string
  status: 'success' | 'error'
  error: string | null
  created_at: string
}

interface SimStatus {
  enabled: boolean
  seeded: boolean
  lastRun: string | null
  agents: AgentStatus[]
}

const ROLE_LABELS: Record<string, string> = {
  corp_safety: 'Corporate Safety',
  ops_safety: 'Operations Safety',
  supervisor: 'Supervisor',
  tenant_admin: 'Admin',
}

const TOOL_LABELS: Record<string, string> = {
  create_incident: 'Logged Incident',
  create_work_permit: 'Created Permit',
  review_and_comment_incident: 'Reviewed Incident',
  create_corrective_action: 'Raised Action',
  complete_corrective_action: 'Completed Action',
  approve_permit: 'Approved Permit',
  error: 'Error',
}

const ROLE_COLORS: Record<string, string> = {
  corp_safety: '#0052CC',
  ops_safety: '#00875A',
  supervisor: '#FF8B00',
}

type ScenarioEventType = 'stage' | 'step_start' | 'step_done' | 'step_error' | 'compliance_action' | 'complete' | 'fatal'

interface ScenarioEvent {
  type: ScenarioEventType
  stage?: string
  stageLabel?: string
  label?: string
  agentId?: string
  agentName?: string
  agentRole?: string
  initials?: string
  tool?: string
  description?: string
  entityId?: string
  entityType?: string
  number?: string
  action?: string
  icon?: string
  delivered?: boolean
  error?: string
  message?: string
  notifiableIncidentId?: string
}

const STAGE_LABELS: Record<string, string> = {
  '1': 'Field Worker Issues',
  '2': 'Supervisor Assessment',
  '3': 'Safety Officer Response',
  '4': 'Compliance Notifications',
  '5': 'Close Out',
}

const TOOL_ICONS: Record<string, string> = {
  create_incident: '⚠️',
  create_work_permit: '📋',
  review_and_comment_incident: '💬',
  create_corrective_action: '✅',
  complete_corrective_action: '🏁',
  approve_permit: '✔️',
}

function fmt(dt: string) {
  return new Date(dt).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// ── Scenario modal ────────────────────────────────────────────────────────────
function ScenarioModal({ onClose }: { onClose: () => void }) {
  const { accessToken } = useAuthStore()
  const [events, setEvents] = useState<ScenarioEvent[]>([])
  const [running, setRunning] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeStep, setActiveStep] = useState<string | null>(null)
  const esRef = useRef<EventSource | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return () => { esRef.current?.close() }
  }, [])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [events])

  const startScenario = () => {
    if (!accessToken || running) return
    setRunning(true)
    setEvents([])
    setDone(false)
    setError(null)

    const url = `/api/simulation/scenario/stream?token=${encodeURIComponent(accessToken)}`
    const es = new EventSource(url)
    esRef.current = es

    const addEvent = (type: ScenarioEventType, data: Record<string, unknown>) =>
      setEvents(prev => [...prev, { type, ...data } as ScenarioEvent])

    es.addEventListener('stage', e => {
      const d = JSON.parse(e.data)
      addEvent('stage', d)
    })
    es.addEventListener('step_start', e => {
      const d = JSON.parse(e.data)
      setActiveStep(d.agentId + d.tool)
      addEvent('step_start', d)
    })
    es.addEventListener('step_done', e => {
      const d = JSON.parse(e.data)
      setActiveStep(null)
      addEvent('step_done', d)
    })
    es.addEventListener('step_error', e => {
      const d = JSON.parse(e.data)
      setActiveStep(null)
      addEvent('step_error', d)
    })
    es.addEventListener('compliance_action', e => {
      addEvent('compliance_action', JSON.parse(e.data))
    })
    es.addEventListener('complete', e => {
      addEvent('complete', JSON.parse(e.data))
      setDone(true)
      setRunning(false)
      es.close()
    })
    es.addEventListener('fatal', e => {
      const d = JSON.parse(e.data)
      setError(d.error)
      setRunning(false)
      es.close()
    })
    es.onerror = () => {
      if (!done) {
        setError('Connection lost')
        setRunning(false)
      }
      es.close()
    }
  }

  const STAGE_COLORS: Record<string, string> = {
    '1': '#DE350B', '2': '#FF8B00', '3': '#0052CC', '4': '#6554C0', '5': '#00875A',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-2xl rounded-2xl flex flex-col" style={{ background: 'white', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            <Clapperboard size={20} style={{ color: 'var(--accent)' }} />
            <div>
              <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Live Scenario — End-to-End Test</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Field incidents → supervisor response → safety actions → compliance → close out</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}>
            <X size={16} />
          </button>
        </div>

        {/* Start button or stream */}
        {events.length === 0 && !running ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
            <div className="text-center" style={{ color: 'var(--text-muted)' }}>
              <Clapperboard size={40} style={{ margin: '0 auto 12px', color: 'var(--accent)', opacity: 0.6 }} />
              <div className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>5-Stage Live Scenario</div>
              <div className="text-sm">Lisa Park logs incidents → James Wilson + Tony assess → Sarah Chen + Mike + Emma respond → compliance notifications (real SMS + email to you) → close out</div>
              <div className="mt-3 text-xs px-3 py-1.5 rounded-full inline-block" style={{ background: 'rgba(222,53,11,0.08)', color: '#DE350B' }}>
                ⚠️ Will send real SMS and email to your number
              </div>
            </div>
            <button onClick={startScenario} className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm"
              style={{ background: 'var(--accent)' }}>
              <Play size={16} /> Start Live Scenario
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
            {events.map((ev, i) => {
              if (ev.type === 'stage') {
                return (
                  <div key={i} className="flex items-center gap-2 pt-3 pb-1">
                    <div className="h-px flex-1" style={{ background: 'var(--border)' }} />
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                      style={{ background: STAGE_COLORS[ev.stage || '1'] || 'var(--accent)' }}>
                      Stage {ev.stage}: {ev.label}
                    </span>
                    <div className="h-px flex-1" style={{ background: 'var(--border)' }} />
                  </div>
                )
              }

              if (ev.type === 'step_start') {
                return (
                  <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-lg" style={{ background: 'rgba(0,82,204,0.04)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ background: 'var(--accent)' }}>{ev.initials}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{ev.agentName}</div>
                      <div className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                        {TOOL_ICONS[ev.tool || ''] || '🔄'} Running {TOOL_LABELS[ev.tool || ''] || ev.tool}…
                      </div>
                    </div>
                    <RefreshCw size={13} className="animate-spin flex-shrink-0" style={{ color: 'var(--accent)' }} />
                  </div>
                )
              }

              if (ev.type === 'step_done') {
                return (
                  <div key={i} className="flex items-start gap-3 py-2 px-3 rounded-lg" style={{ background: 'rgba(0,135,90,0.04)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ background: '#00875A' }}>{ev.initials}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{ev.agentName}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded-full text-white" style={{ background: '#00875A', fontSize: 10 }}>
                          {TOOL_LABELS[ev.tool || ''] || ev.tool}
                        </span>
                        {ev.number && <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{ev.number}</span>}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{ev.description}</div>
                    </div>
                    <CheckCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#00875A' }} />
                  </div>
                )
              }

              if (ev.type === 'step_error') {
                return (
                  <div key={i} className="flex items-start gap-3 py-2 px-3 rounded-lg bg-red-50">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ background: '#DE350B' }}>{ev.initials}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold" style={{ color: '#DE350B' }}>{ev.agentName} — Error</div>
                      <div className="text-xs mt-0.5 text-red-700">{ev.error}</div>
                    </div>
                    <XCircle size={14} className="flex-shrink-0 text-red-500" />
                  </div>
                )
              }

              if (ev.type === 'compliance_action') {
                return (
                  <div key={i} className="flex items-center gap-3 py-1.5 px-3 ml-4 rounded-lg" style={{ background: 'rgba(101,84,192,0.05)' }}>
                    <span className="text-base flex-shrink-0">{ev.icon || '🔔'}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{ev.description}</span>
                    </div>
                    {ev.delivered !== undefined && (
                      ev.delivered
                        ? <CheckCircle size={12} style={{ color: '#00875A', flexShrink: 0 }} />
                        : <XCircle size={12} style={{ color: '#DE350B', flexShrink: 0 }} />
                    )}
                  </div>
                )
              }

              if (ev.type === 'complete') {
                return (
                  <div key={i} className="text-center py-6">
                    <div className="text-2xl mb-2">🎉</div>
                    <div className="font-bold text-sm" style={{ color: '#00875A' }}>Scenario Complete</div>
                    <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      All agents ran. Check the incidents, permits, and corrective actions list to see the generated data.
                      {ev.notifiableIncidentId && (
                        <span> The notifiable incident has a full compliance checklist — open it to see the action panel.</span>
                      )}
                    </div>
                  </div>
                )
              }

              return null
            })}

            {running && events.length > 0 && !done && (
              <div className="flex items-center gap-2 py-3 px-3" style={{ color: 'var(--text-muted)' }}>
                <RefreshCw size={12} className="animate-spin" />
                <span className="text-xs">Agents working…</span>
              </div>
            )}

            {error && (
              <div className="text-xs text-red-600 bg-red-50 p-3 rounded-lg">{error}</div>
            )}

            <div ref={bottomRef} />
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {running ? 'Scenario in progress — real SMS + email sending…' : done ? 'Finished — refresh the activity log' : '11 steps across 5 stages'}
          </div>
          {(done || error) && (
            <button onClick={startScenario} className="text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
              <RefreshCw size={11} /> Run Again
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function SimulationSettings() {
  const [status, setStatus] = useState<SimStatus | null>(null)
  const [logs, setLogs] = useState<SimLog[]>([])
  const [loading, setLoading] = useState(true)
  const [showScenario, setShowScenario] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [runningAll, setRunningAll] = useState(false)
  const [runningAgent, setRunningAgent] = useState<string | null>(null)
  const [removing, setRemoving] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)

  const showNotice = (type: 'success' | 'error', msg: string) => {
    setNotice({ type, msg })
    setTimeout(() => setNotice(null), 4000)
  }

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get('/simulation').then(r => r.data),
        api.get('/simulation/log?limit=30').then(r => r.data),
      ])
      setStatus(s)
      setLogs(l.data)
    } catch {
      showNotice('error', 'Failed to load simulation status')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleToggle = async () => {
    if (!status) return
    setToggling(true)
    try {
      await api.patch('/simulation/config', { enabled: !status.enabled })
      setStatus(s => s ? { ...s, enabled: !s.enabled } : s)
    } catch {
      showNotice('error', 'Failed to update simulation mode')
    } finally {
      setToggling(false)
    }
  }

  const handleSeed = async () => {
    setSeeding(true)
    try {
      await api.post('/simulation/seed')
      showNotice('success', '6 AI agent users created successfully')
      load()
    } catch (err: any) {
      showNotice('error', err.response?.data?.error || 'Failed to seed agents')
    } finally {
      setSeeding(false)
    }
  }

  const handleRunAll = async () => {
    setRunningAll(true)
    try {
      await api.post('/simulation/run')
      showNotice('success', 'All agents running — refresh log in a moment')
      setTimeout(load, 4000)
    } catch (err: any) {
      showNotice('error', err.response?.data?.error || 'Failed to run agents')
    } finally {
      setRunningAll(false)
    }
  }

  const handleRunAgent = async (agentId: string) => {
    setRunningAgent(agentId)
    try {
      await api.post(`/simulation/run/${agentId}`)
      showNotice('success', 'Agent running — refresh log in a moment')
      setTimeout(load, 3000)
    } catch (err: any) {
      showNotice('error', err.response?.data?.error || 'Failed to run agent')
    } finally {
      setRunningAgent(null)
    }
  }

  const handleRemove = async () => {
    if (!confirm('Remove all simulated agent users and their activity log? System data they created (incidents, permits, etc.) will remain.')) return
    setRemoving(true)
    try {
      await api.delete('/simulation/users')
      showNotice('success', 'Simulation users removed')
      load()
    } catch (err: any) {
      showNotice('error', err.response?.data?.error || 'Failed to remove agents')
    } finally {
      setRemoving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-6 h-6 rounded-full border-2 border-t-transparent" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
      </div>
    )
  }

  const allSeeded = status?.agents.every(a => a.seeded)

  return (
    <div className="max-w-4xl">
      {showScenario && <ScenarioModal onClose={() => { setShowScenario(false); load() }} />}

      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Bot size={22} style={{ color: 'var(--accent)' }} />
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>AI Simulation Agents</h1>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Autonomous AI personas that act as real professionals to populate the system with realistic data for end-to-end testing.
        </p>
      </div>

      {notice && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${notice.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
          {notice.type === 'success' ? <CheckCircle size={15} /> : <XCircle size={15} />}
          {notice.msg}
        </div>
      )}

      {/* Control panel */}
      <div className="rounded-xl border p-5 mb-6" style={{ borderColor: 'var(--border)', background: 'white' }}>
        <div className="flex flex-wrap items-center gap-4">
          {/* Enable toggle */}
          <div className="flex items-center gap-3 flex-1 min-w-48">
            <button
              onClick={handleToggle}
              disabled={toggling}
              className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
              style={{ background: status?.enabled ? 'var(--accent)' : 'var(--border)' }}
            >
              <span
                className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: status?.enabled ? 'translateX(20px)' : 'translateX(0)' }}
              />
            </button>
            <div>
              <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {status?.enabled ? 'Simulation Enabled' : 'Simulation Disabled'}
              </div>
              {status?.lastRun && (
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Last run: {fmt(status.lastRun)}</div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {!allSeeded ? (
              <button
                onClick={handleSeed}
                disabled={seeding}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity"
                style={{ background: 'var(--accent)', opacity: seeding ? 0.7 : 1 }}
              >
                {seeding ? <RefreshCw size={14} className="animate-spin" /> : <UserPlus size={14} />}
                {seeding ? 'Creating...' : 'Seed Agents'}
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowScenario(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity"
                  style={{ background: '#6554C0' }}
                >
                  <Clapperboard size={14} /> Run Live Scenario
                </button>
                <button
                  onClick={handleRunAll}
                  disabled={runningAll}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity"
                  style={{ background: 'var(--accent)', opacity: runningAll ? 0.7 : 1 }}
                >
                  {runningAll ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
                  {runningAll ? 'Running...' : 'Run All Agents'}
                </button>
              </>
            )}

            {allSeeded && (
              <button
                onClick={handleRemove}
                disabled={removing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all hover:bg-red-50"
                style={{ borderColor: 'var(--red)', color: 'var(--red)', opacity: removing ? 0.7 : 1 }}
              >
                {removing ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {removing ? 'Removing...' : 'Remove Agents'}
              </button>
            )}

            <button
              onClick={load}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all hover:bg-gray-50"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Agent cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {status?.agents.map(agent => (
          <div
            key={agent.id}
            className="rounded-xl border p-4"
            style={{ borderColor: 'var(--border)', background: 'white', opacity: agent.seeded ? 1 : 0.6 }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ background: ROLE_COLORS[agent.role] || 'var(--accent)' }}
              >
                {agent.firstName[0]}{agent.lastName[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                    {agent.firstName} {agent.lastName}
                  </span>
                  {agent.seeded ? (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>
                  ) : (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">Not seeded</span>
                  )}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{agent.jobTitle}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{ROLE_LABELS[agent.role] || agent.role}</div>

                {agent.lastAction && (
                  <div className="mt-2 text-xs p-2 rounded-lg" style={{ background: 'var(--bg)' }}>
                    <span className="font-medium" style={{ color: 'var(--accent)' }}>{TOOL_LABELS[agent.lastAction.tool] || agent.lastAction.tool}: </span>
                    <span style={{ color: 'var(--text-secondary)' }}>{agent.lastAction.description.slice(0, 80)}</span>
                    <div className="mt-0.5" style={{ color: 'var(--text-muted)' }}>{fmt(agent.lastAction.created_at)}</div>
                  </div>
                )}
              </div>

              {agent.seeded && (
                <button
                  onClick={() => handleRunAgent(agent.id)}
                  disabled={runningAgent === agent.id}
                  title="Run this agent"
                  className="flex-shrink-0 p-2 rounded-lg border transition-all hover:bg-gray-50"
                  style={{ borderColor: 'var(--border)', color: 'var(--accent)' }}
                >
                  {runningAgent === agent.id
                    ? <RefreshCw size={15} className="animate-spin" />
                    : <PlayCircle size={15} />}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Activity log */}
      <div className="rounded-xl border" style={{ borderColor: 'var(--border)', background: 'white' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Agent Activity Log</h2>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{logs.length} entries</span>
        </div>

        {logs.length === 0 ? (
          <div className="py-12 text-center">
            <Clock size={28} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No agent activity yet. Seed agents and run a cycle to see results.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {logs.map(log => (
              <div key={log.id} className="flex items-start gap-3 px-4 py-3">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${log.status === 'error' ? 'bg-red-100' : 'bg-green-100'}`}
                >
                  {log.status === 'error'
                    ? <XCircle size={13} className="text-red-600" />
                    : <CheckCircle size={13} className="text-green-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{log.agent_name}</span>
                    {log.tool !== 'error' && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg)', color: 'var(--accent)' }}>
                        {TOOL_LABELS[log.tool] || log.tool}
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {log.status === 'error' ? log.error || log.description : log.description}
                  </p>
                </div>
                <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{fmt(log.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
